import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext } from 'react'
import useEntity from '../hooks/useEntity'
import AuthorizationContext from './AuthorizationContext'
import { UserListItemDto } from '../config/userConfig'
import { DocumentContext, FolderType, IDefaultFolderDto } from '../config/documentConfig'
import { DocumentType } from '../config/documentConfig'
import { FilePartyRole } from '../config/filePartyConfig'
import { DimensionsDto } from '../components/Form/Accounting/DimensionFields'
import { LegalAidCertificateSettings } from '../components/Form/LegalAidCertificate/LegalAidCertificateForm'
import { AuthorizationGroupDto } from '../components/Combobox/AuthorizationGroupCombobox'
import { Country, Currency, I18nNomenclature, Language, LegalStructure, SurchargeType, VatType } from '../config/i18nConfig'
import { AttributeDefinitionDto } from '../hooks/useAttributes'
import { DisbursementType, FixedPriceType } from '../config/registrationConfig'
import { Billable } from '../config/registration/timeConfig'
import { Gender } from '../config/contactConfig'
import { InvoiceMessage } from '../config/finance/invoiceConfig'
import { WorkflowDto } from '../components/TagPicker/WorkflowPicker'
import { EntityType } from '../components/Form/Accounting/EntityField'
import { OrganisationProfileDto } from '../components/Combobox/OrganisationProfileCombobox'

export enum ModuleType {
    Accounting = 'ACCOUNTING',
    Attributes = 'ATTRIBUTES',
    Authorization = 'AUTHORIZATION',
    AuditTrail = 'AUDIT_TRAIL',
    AuditTrailTracking = 'AUDIT_TRAIL_TRACKING',
    BudgetMonitoring = 'BUDGET_MONITORING',
    CRM = 'CRM',
    Disbursements = 'DISBURSEMENTS',
    Docket = 'DOCKET',
    FamilyLaw = 'FAMILY_LAW',
    FixedPrices = 'FIXED_PRICES',
    Injury = 'INJURY',
    Invoices = 'INVOICES',
    InvoiceApproval = 'INVOICE_APPROVAL',
    Insolvency = 'INSOLVENCY',
    LegalAid = 'LEGAL_AID',
    Mollie = 'MOLLIE',
    ProofMe = 'PROOF_ME',
    Reports = 'REPORTS',
    ReportsPro = 'REPORTS_PRO',
    Tasks = 'TASKS',
    TimeCategories = 'TIME_CATEGORIES',
    TimeTracking = 'TIME_TRACKING',
    TravelExpenses = 'TRAVEL_EXPENSES',
    Workflow = 'WORKFLOW',
    WorkInProgress = 'WORK_IN_PROGRESS',
}

export type ModuleDto = {
    type: ModuleType
    fileModule: boolean
    defaultActiveForFile: boolean
}

export enum Accounting {
    Exact = 'EXACT',
    Twinfield = 'TWINFIELD',
}

export type DashboardDto = {
    enabled?: boolean
    showRecentFiles?: boolean
}

export type DocumentDto = {
    uri: string
    label: string
    type: DocumentType
    version?: number
    context: DocumentContext
    name?: string
    folder?: FolderType
    role?: FilePartyRole | string
}

export enum DocumentOpenType {
    Open = 'OPEN',
    Download = 'DOWNLOAD',
}

export type DocumentsSharing = {
    enabled?: boolean
    name: string
    driveId: string
}

export type FilePartyRoleDto = {
    type: FilePartyRole | string
    label: string
}

export enum FileRateType {
    File = 'FILE',
    PartyTier = 'PARTY_TIER',
    UserTier = 'USER_TIER',
}

export type FileScaffoldAuthorizationDto = {
    individuals: string[]
    groups: string[]
}

export type FileScaffoldDto = {
    accounting: DimensionsDto
    authorization: FileScaffoldAuthorizationDto
    billable: Billable
    currency: string
    fileRate: FileRateDto
    language: string
    paymentCurrency: string
    unit: string
    vatType: string
}

export type FileRateDto = {
    type: FileRateType
    hourRate: string
    surcharges?: string[]
}

export enum SortDirection {
    Ascending = 'ASC',
    Descending = 'DESC',
}

export type FileSortDto = {
    direction: SortDirection
    field: string
}

export type UnitDto = {
    key: string
    name: string
    enabled: boolean
    accounting: Accounting
    dimensions: EntityType[]
}

export type GlobalContextType = {
    settings?: SettingsDto
    attributeDefinitions?: AttributeDefinitionDto[]
    authorizationGroups?: AuthorizationGroupDto[]
    countries?: Country[]
    currencies?: Currency[]
    disbursementTypes?: DisbursementType[]
    fixedPriceTypes?: FixedPriceType[]
    languages?: Language[]
    legalStructures?: LegalStructure[]
    organisationProfiles?: OrganisationProfileDto[]
    surchargeTypes?: SurchargeType[]
    taskStatuses?: TaskStatus[]
    units?: UnitDto[]
    users?: UserListItemDto[]
    vatTypes?: VatType[]
    workflows?: WorkflowDto[]
    loaded: boolean
    reset?: () => void
}

export type InvoiceRecipientDto = {
    clients?: string[]
    debtors?: string[]
}

export type InsolvencySetting = {
    factorEstate: string
    rateEstate: string
    roles?: InsolvencyRole[]
}

type InsolvencyRole = {
    key: string
    name: string
}

export type LocalizedValueSetting<K = string, V = string> = {
    key: K
    value: V
}

export type TaskConfiguration = {
    expectedTime?: boolean
    categories?: TaskCategory[]
}

export type TaskCategory = {
    key: string
    name: string
    color?: string
    isDefault?: boolean
    description?: string
}

export type TaskStatus = I18nNomenclature & {
    alpha: string
    isStartStatus?: boolean
    isEndStatus?: boolean
}

export enum ThemeOption {
    Light = 'LIGHT',
    Dark = 'DARK',
    Auto = 'AUTO',
}

export enum TimeSuggestionDescriptionOptionEnum {
    EmailIn = 'EMAIL_IN',
    EmailOut = 'EMAIL_OUT',
}

export enum TimeCategoryDefaultEnum {
    DefaultEmailIn = 'DEFAULT_EMAIL_IN',
    DefaultEmailOut = 'DEFAULT_EMAIL_OUT',
    Default = 'DEFAULT',
}

export type TimeCategory = {
    key: string
    name: string
    module?: ModuleType
    defaultDescription?: string
    appendDescription?: boolean
    defaultType?: TimeCategoryDefaultEnum
    billable?: Billable
}

export enum TimeSuggestionOptionEnum {
    None = 'NONE',
    Notification = 'NOTIFICATION',
    Popup = 'POPUP',
}

export type TimeSuggestionDto = {
    fileSingleEmail?: TimeSuggestionOptionEnum
    fileMultipleEmail?: TimeSuggestionOptionEnum
    incomingEmail?: TimeSuggestionOptionEnum
    sentEmail?: TimeSuggestionOptionEnum
}

export type SettingsDto = {
    modules?: ModuleDto[]
    cocNumber?: number
    crmNoDefaultFile?: boolean
    dashboard?: DashboardDto
    documents?: DocumentDto[]
    documentsOpen?: DocumentOpenType
    documentsSharing?: DocumentsSharing
    fileExchangeDisplayName?: string
    fileSharepointDisplayName?: string
    filePartyRoles?: FilePartyRoleDto[]
    fileReference?: any
    fileScaffold?: FileScaffoldDto
    fileSort?: FileSortDto
    folders?: IDefaultFolderDto[]
    ibanNumber?: string
    invoiceEmailBcc?: string[]
    invoiceEmailMessages?: InvoiceMessage[]
    invoicePaymentCurrency?: boolean
    invoicePaymentMessages?: InvoiceMessage[]
    invoiceRecipient?: InvoiceRecipientDto
    invoiceNumber?: any
    insolvency?: InsolvencySetting
    legalAidCertificate?: LegalAidCertificateSettings
    locality?: string
    partySalutation?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    partyAttentionOf?: LocalizedValueSetting<string, LocalizedValueSetting<Gender>[]>[]
    task?: TaskConfiguration
    termInvoice?: number
    termPayment?: number
    timeCategories?: TimeCategory[]
    timeDivisor?: number
    timeSuggestion?: TimeSuggestionDto
    timeSuggestionDescription?: LocalizedValueSetting<string, LocalizedValueSetting<TimeSuggestionDescriptionOptionEnum>[]>[]
    timeSuggestionNotificationsDays?: number
    vatNumber?: string
}

const GlobalContext = createContext<GlobalContextType>(null)

const getter = (url: string) => () => ({ method: 'GET', url })

export function GlobalProvider(props: PropsWithChildren): ReactElement {
    // Re-fetch /global whenever the (user, environment) tuple changes. This is a defensive dependency.
    // See AuthorizationContext for the contract on `environmentKey`.
    const { environmentKey } = useContext(AuthorizationContext)
    const [data, , resetSettings] = useEntity<GlobalContextType>(getter('/global'), false, undefined, undefined, undefined, [environmentKey])

    return (
        <GlobalContext.Provider
            value={{
                ...data.data,
                loaded: data.loaded,
                reset: () => resetSettings(),
            }}
        >
            {props.children}
        </GlobalContext.Provider>
    )
}

export default GlobalContext
